import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Tag, Radio, ShoppingCart, ChevronDown, Menu, X,
  User, LogOut, LayoutDashboard, Heart, Wand2, Sparkles, KeyRound, LifeBuoy,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import { CategoryMenu, MobileCategories } from "@/components/CategoryMenu";
import { api } from "@/lib/api";
import SearchBox from "@/components/SearchBox";
import { needsOnboarding, needsBilling } from "@/lib/onboarding";
import { CROSSLIST_LINKS, AUTOMATION_LINKS, crosslistActive, automationsActive, marketplacesActive } from "@/components/SellerToolsNav";
import { useQueryClient } from "@tanstack/react-query";
import Logo from "@/components/Logo";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const FEATURE_MENU = [
  {
    label: "Shop",
    items: [
      { to: "/features/compare-prices", label: "Compare prices", blurb: "Best offer across stores" },
      { to: "/features/ai-search", label: "AI search", blurb: "Ask — get matching items" },
      { to: "/features/virtual-try-on", label: "Virtual try-on", blurb: "Preview on you" },
      { to: "/features/live-selling", label: "Live selling", blurb: "Watch and buy in-room" },
    ],
  },
  {
    label: "Sell",
    items: [
      { to: "/features/crosslist", label: "Crosslist", blurb: "One listing, many shops" },
      { to: "/features/auto-delist", label: "Auto-delist", blurb: "Never double-sell" },
      { to: "/features/ai-photoshoots", label: "AI photoshoots", blurb: "Studio shots from a flat lay" },
      { to: "/features/marketplaces", label: "Marketplaces", blurb: "eBay, Etsy, Poshmark…" },
    ],
  },
  {
    label: "Agents",
    items: [
      { to: "/features/graph-api", label: "Graph API", blurb: "Build shopping agents, earn on sales" },
      { to: "/features/affiliate-rails", label: "Affiliate rails", blurb: "Rakuten, CJ, Skimlinks" },
      { to: "/features/product-graph", label: "Product graph", blurb: "One item, many offers" },
      { to: "/features/all-capabilities", label: "All capabilities", blurb: "Every feature in one place" },
    ],
  },
];

const FEATURE_PATHS = ["/features"];

export default function Navbar() {
  const { user, logout, stopImpersonating } = useAuth();
  const qc = useQueryClient();
  const { country, currency, setCountry, setCurrency, countries, currencies } = useLocale();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [count, setCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { data: categoryTree } = useQuery({
    queryKey: ["catalog-categories", country],
    queryFn: async () => (await api.get("/catalog/categories", { params: { country } })).data,
    staleTime: 5 * 60 * 1000,
  });
  const brands = categoryTree?.brands || [];

  const refreshCart = async () => {
    if (!user || user.role !== "buyer") return setCount(0);
    try {
      const { data } = await api.get("/cart");
      setCount(data.items.reduce((s, i) => s + i.quantity, 0));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    refreshCart();
    const h = () => refreshCart();
    window.addEventListener("cart-updated", h);
    return () => window.removeEventListener("cart-updated", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const q = new URLSearchParams(search).get("q") || "";
    if (pathname === "/shop") setQuery(q);
  }, [pathname, search]);

  const isSeller = user?.role === "seller";
  const isAdmin = user?.role === "admin";
  const isDeveloper = user?.role === "developer";
  const params = new URLSearchParams(search);
  const activeCategory = params.get("category");
  const activeDept = params.get("dept");
  const activeBrand = params.get("brand");
  const shopActive = pathname === "/shop";

  const sellerLinks = [
    { to: "/seller", label: "Dashboard" },
    { to: "/seller/orders", label: "Orders" },
    { to: "/profile", label: "Profile" },
    { to: "/shop", label: "Storefront" },
    { to: "/seller/live", label: "Go Live" },
  ];
  const adminLinks = [
    { to: "/admin", label: "Settings" },
    { to: "/shop", label: "Storefront" },
    { to: "/developers/console", label: "Developer console" },
  ];
  const developerLinks = [
    { to: "/developers/console", label: "Console" },
    { to: "/developers", label: "Graph API" },
    { to: "/shop", label: "Storefront" },
  ];
  const roleLinks = isAdmin ? adminLinks : isSeller ? sellerLinks : isDeveloper ? developerLinks : null;

  const goShop = (extra = {}) => {
    const next = new URLSearchParams();
    if (extra.dept) next.set("dept", extra.dept);
    if (extra.category) next.set("category", extra.category);
    if (extra.sub) next.set("sub", extra.sub);
    if (extra.brand) next.set("brand", extra.brand);
    if (extra.q) next.set("q", extra.q);
    setMobileOpen(false);
    navigate(next.toString() ? `/shop?${next}` : "/shop");
  };
  const openBrand = (brand) => {
    goShop({ brand: brand.id });
  };

  const navLink = (active) =>
    `inline-flex items-center gap-2 text-sm font-medium whitespace-nowrap ${
      active ? "text-aura-accent" : "text-aura-ink/80 hover:text-aura-ink"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-aura-bg/95 backdrop-blur-md border-b border-aura-border" data-testid="navbar">
      {user?.impersonated_by && (
        <div className="bg-aura-accent text-white text-xs px-4 py-2 flex items-center justify-center gap-3" data-testid="impersonation-banner">
          <span>Viewing {user.name}’s {user.role} account</span>
          <button
            type="button"
            className="underline underline-offset-2 font-medium"
            data-testid="exit-impersonation"
            onClick={async () => {
              await stopImpersonating();
              qc.clear();
              navigate("/admin");
            }}
          >
            Exit to admin
          </button>
        </div>
      )}
      {needsBilling(user) && pathname !== "/seller/subscribe" && (
        <div className="bg-aura-accent text-white text-xs px-4 py-2 flex items-center justify-center gap-3" data-testid="billing-banner">
          <span>Choose a seller plan to open the studio. Shoppers stay free.</span>
          <Link to="/seller/subscribe" className="underline underline-offset-2 font-medium">
            View plans
          </Link>
        </div>
      )}
      {needsOnboarding(user) && pathname !== "/seller/onboarding" && !needsBilling(user) && (
        <div className="bg-aura-surface border-b border-aura-border text-xs px-4 py-2 flex items-center justify-center gap-3" data-testid="onboarding-banner">
          <span>Finish setting up your shop to publish and crosslist.</span>
          <Link to="/seller/onboarding" className="underline underline-offset-2 font-medium text-aura-accent">
            Continue setup
          </Link>
        </div>
      )}
      <div className="relative max-w-[1440px] mx-auto px-4 lg:px-6 h-[72px] flex items-center gap-3 lg:gap-5">
        <Link to="/" className="flex items-center shrink-0 pr-3 lg:pr-4 border-r border-aura-border h-9" data-testid="logo-link">
          <Logo size={34} />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 shrink-0">
          {isSeller ? (
            <>
              <Link to="/seller" data-testid="nav-dashboard" className={navLink(pathname === "/seller")}>Dashboard</Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-crosslist" className={`${navLink(crosslistActive(pathname))} outline-none inline-flex items-center gap-1`}>
                    Crosslist <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {CROSSLIST_LINKS.map((l) => (
                    <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)}>{l.label}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-automations" className={`${navLink(automationsActive(pathname))} outline-none inline-flex items-center gap-1`}>
                    Automations <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {AUTOMATION_LINKS.map((l) => (
                    <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)}>{l.label}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/seller/connections" data-testid="nav-marketplaces" className={navLink(marketplacesActive(pathname))}>Marketplaces</Link>
              {sellerLinks.filter((l) => l.to !== "/seller").map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={navLink(pathname === l.to.split("?")[0])}
                >
                  {l.label}
                </Link>
              ))}
            </>
          ) : roleLinks ? (
            roleLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={navLink(pathname === l.to.split("?")[0])}
              >
                {l.label}
              </Link>
            ))
          ) : (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-features" className={`${navLink(FEATURE_PATHS.some((p) => pathname.startsWith(p)))} outline-none`}>
                    <Sparkles className="w-4 h-4" />
                    Features
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[min(92vw,640px)] p-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {FEATURE_MENU.map((group) => (
                      <div key={group.label}>
                        <DropdownMenuLabel className="text-[10px] tracking-luxe uppercase text-aura-muted font-normal px-2">
                          {group.label}
                        </DropdownMenuLabel>
                        {group.items.map((item) => (
                          <DropdownMenuItem
                            key={`${group.label}-${item.label}`}
                            onClick={() => { setMobileOpen(false); navigate(item.to); }}
                            className="flex-col items-start gap-0.5 py-2"
                          >
                            <span className="text-sm font-medium">{item.label}</span>
                            <span className="text-[11px] text-aura-muted leading-snug">{item.blurb}</span>
                          </DropdownMenuItem>
                        ))}
                      </div>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              <CategoryMenu
                tree={categoryTree}
                onShop={goShop}
                linkClass={navLink(shopActive && (!!activeCategory || !!activeDept))}
              />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-brands" className={`${navLink(!!activeBrand)} outline-none`}>
                    <Tag className="w-4 h-4" />
                    Brands
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 max-h-80 overflow-y-auto">
                  {brands.length === 0 ? (
                    <DropdownMenuItem disabled>No brands in this category</DropdownMenuItem>
                  ) : (
                    brands.map((b) => (
                      <DropdownMenuItem
                        key={b.id}
                        onClick={() => openBrand(b)}
                        data-testid={`nav-brand-${(b.label || b.name || "").toLowerCase().replace(/\s+/g, "-")}`}
                        className={activeBrand === b.id ? "text-aura-accent" : ""}
                      >
                        {b.label || b.name}
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/live" data-testid="nav-live" className={navLink(pathname === "/live")}>
                <Radio className="w-4 h-4" />
                Live
              </Link>
              {country === "IN" ? (
                <Link to="/deals" data-testid="nav-offers-deals" className={navLink(pathname === "/deals")}>
                  Offers & Deals
                </Link>
              ) : null}
            </>
          )}
        </nav>

        <SearchBox
          value={query}
          onChange={setQuery}
          onSearch={(q) => goShop(q ? { q } : {})}
          testId="nav-search"
          className="hidden md:block flex-1 min-w-0 max-w-2xl mx-1"
        />

        <div className="flex items-center gap-2 sm:gap-3 ml-auto shrink-0">
          {!isSeller && !isAdmin && (
            <>
              <Link
                to={user ? "/looks" : "/auth?mode=login"}
                className="hidden sm:flex w-9 h-9 items-center justify-center text-aura-ink/80 hover:text-aura-ink"
                data-testid="nav-wishlist"
                aria-label="Saved"
              >
                <Heart className="w-[18px] h-[18px]" />
              </Link>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-aura-ink/80 hover:text-aura-ink outline-none px-1"
                    data-testid="locale-picker"
                    aria-label="Language and shipping"
                  >
                    {country}
                    <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-[10px] tracking-luxe uppercase text-aura-muted font-normal">Ship to</DropdownMenuLabel>
                  {(countries || []).map((c) => (
                    <DropdownMenuItem
                      key={c.code}
                      onClick={() => setCountry(c.code)}
                      data-testid={`country-${c.code}`}
                      className={country === c.code ? "text-aura-accent" : ""}
                    >
                      {c.name}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] tracking-luxe uppercase text-aura-muted font-normal">Currency</DropdownMenuLabel>
                  {(currencies || []).map((c) => (
                    <DropdownMenuItem
                      key={c.code}
                      onClick={() => setCurrency(c.code)}
                      data-testid={`currency-${c.code}`}
                      className={currency === c.code ? "text-aura-accent" : ""}
                    >
                      {c.code} · {c.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link
                to={user ? "/cart" : "/auth?mode=login"}
                className="relative flex w-9 h-9 items-center justify-center text-aura-ink/80 hover:text-aura-ink"
                data-testid="cart-link"
                aria-label="Bag"
              >
                <ShoppingCart className="w-[18px] h-[18px]" />
                {count > 0 && (
                  <span className="absolute top-0.5 right-0.5 bg-aura-accent text-white text-[10px] min-w-4 h-4 px-1 rounded-full flex items-center justify-center">
                    {count}
                  </span>
                )}
              </Link>
            </>
          )}

          <Link to="/contact" className="hidden sm:inline text-sm text-aura-muted hover:text-aura-ink px-1" data-testid="support-link">
            Support
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center" data-testid="account-menu">
                  <div className="w-8 h-8 rounded-full bg-aura-accent text-white flex items-center justify-center text-xs font-semibold">
                    {user.name?.[0]?.toUpperCase()}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-xs text-aura-muted">{user.email}</div>
                <DropdownMenuSeparator />
                {user.impersonated_by && (
                  <DropdownMenuItem onClick={async () => { await stopImpersonating(); qc.clear(); navigate("/admin"); }} data-testid="menu-exit-admin">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Exit to admin
                  </DropdownMenuItem>
                )}
                {isAdmin ? (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Settings
                  </DropdownMenuItem>
                ) : isSeller ? (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/seller")} data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                      <User className="w-4 h-4 mr-2" /> Profile & shipping
                    </DropdownMenuItem>
                  </>
                ) : isDeveloper ? (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/developers/console")} data-testid="menu-dev-console">
                      <KeyRound className="w-4 h-4 mr-2" /> Developer console
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                      <User className="w-4 h-4 mr-2" /> Profile
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                      <User className="w-4 h-4 mr-2" /> Fit Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                      <ShoppingCart className="w-4 h-4 mr-2" /> Orders
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/looks")} data-testid="menu-looks">
                      <Heart className="w-4 h-4 mr-2" /> Saved
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/tryon")} data-testid="menu-tryon">
                      <Wand2 className="w-4 h-4 mr-2" /> Virtual Try-On
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile#password")} data-testid="menu-password">
                  <KeyRound className="w-4 h-4 mr-2" /> Password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/contact")} data-testid="menu-support">
                  <LifeBuoy className="w-4 h-4 mr-2" /> Support
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <Link
                to="/auth?mode=register"
                data-testid="signup-link"
                className="text-sm font-semibold px-4 h-9 inline-flex items-center rounded-md btn-accent"
              >
                Sign Up
              </Link>
              <Link
                to="/auth?mode=login"
                data-testid="signin-link"
                className="text-sm font-semibold px-4 h-9 inline-flex items-center rounded-md bg-white text-black hover:bg-white/90"
              >
                Login
              </Link>
            </div>
          )}

          <button className="lg:hidden p-1" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-aura-border px-4 py-4 flex flex-col gap-3 bg-aura-bg">
          <SearchBox
            value={query}
            onChange={setQuery}
            onSearch={(q) => goShop(q ? { q } : {})}
            testId="mobile-nav-search"
            className="md:hidden"
          />

          {isSeller ? (
            <>
              <Link to="/seller" onClick={() => setMobileOpen(false)} className="text-sm font-medium py-1">Dashboard</Link>
              <div className="text-[10px] tracking-luxe uppercase text-aura-muted mt-1">Crosslist</div>
              {CROSSLIST_LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="text-sm py-0.5 text-aura-ink/80">{l.label}</Link>
              ))}
              <div className="text-[10px] tracking-luxe uppercase text-aura-muted mt-1">Automations</div>
              {AUTOMATION_LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="text-sm py-0.5 text-aura-ink/80">{l.label}</Link>
              ))}
              <Link to="/seller/connections" onClick={() => setMobileOpen(false)} className="text-sm font-medium py-1">Marketplaces</Link>
              {sellerLinks.filter((l) => l.to !== "/seller").map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="text-sm font-medium py-1">{l.label}</Link>
              ))}
            </>
          ) : roleLinks ? (
            roleLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className="text-sm font-medium py-1"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </Link>
            ))
          ) : (
            <>
              <div className="text-[10px] tracking-luxe uppercase text-aura-muted">Features</div>
              {FEATURE_MENU.map((group) => (
                <div key={group.label}>
                  <div className="text-[10px] tracking-luxe uppercase text-aura-muted/80 mt-2 mb-1">{group.label}</div>
                  {group.items.map((item) => (
                    <Link
                      key={`${group.label}-${item.label}`}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className="block text-sm py-0.5 text-aura-ink/80"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              ))}
              <MobileCategories tree={categoryTree} onShop={goShop} />
              {brands.length > 0 && (
                <>
                  <div className="text-[10px] tracking-luxe uppercase text-aura-muted mt-2">Brands</div>
                  {brands.slice(0, 8).map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => openBrand(b)}
                      className="text-sm text-left text-aura-ink/70 py-0.5"
                    >
                      {b.label || b.name}
                    </button>
                  ))}
                </>
              )}
              <Link to="/live" onClick={() => setMobileOpen(false)} className="text-sm font-medium py-1" data-testid="mobile-nav-live">
                Live
              </Link>
              {country === "IN" ? (
                <Link to="/deals" onClick={() => setMobileOpen(false)} className="text-sm font-medium py-1" data-testid="mobile-nav-offers-deals">
                  Offers & Deals
                </Link>
              ) : null}
            </>
          )}

          {!isSeller && !isAdmin && (
            <div className="flex items-center gap-2 py-1">
              <select value={country} onChange={(e) => setCountry(e.target.value)} className="bg-transparent text-sm outline-none flex-1" data-testid="mobile-country-select">
                {(countries || []).map((c) => (
                  <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                ))}
              </select>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="bg-transparent text-sm outline-none" data-testid="mobile-currency-select">
                {(currencies || []).map((c) => (
                  <option key={c.code} value={c.code}>{c.code}</option>
                ))}
              </select>
            </div>
          )}

          {user && (
            <Link to="/profile#password" onClick={() => setMobileOpen(false)} className="text-sm font-medium py-1" data-testid="mobile-password-link">
              Password
            </Link>
          )}
          <Link to="/contact" onClick={() => setMobileOpen(false)} className="text-sm font-medium py-1" data-testid="mobile-support-link">
            Support
          </Link>

          {!user && (
            <div className="flex gap-2 pt-2 sm:hidden">
              <Link to="/auth?mode=register" onClick={() => setMobileOpen(false)} className="flex-1 text-center text-sm font-semibold py-2.5 rounded-md btn-accent">
                Sign Up
              </Link>
              <Link to="/auth?mode=login" onClick={() => setMobileOpen(false)} className="flex-1 text-center text-sm font-semibold py-2.5 rounded-md bg-white text-black">
                Login
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
